import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Eye, Loader2, Palette, Building2, User, Briefcase, Calculator, FileCheck, X } from 'lucide-react';
import { PAYSTUB_TEMPLATES } from '../TemplateGallery';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pretty = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y ? new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};
const join = (...parts) => parts.filter(Boolean).join(', ') || '—';
const FREQ = { weekly: 'Weekly', 'bi-weekly': 'Bi-weekly', 'semi-monthly': 'Semi-monthly', monthly: 'Monthly' };

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm border-b border-zinc-800/60 last:border-0">
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-100 text-right break-words min-w-0">{value || '—'}</span>
  </div>
);

const Section = ({ icon: Icon, title, step, onEdit, children, testid }) => (
  <section className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-bold text-white flex items-center gap-2"><Icon className="w-4 h-4 text-amber-400" />{title}</h3>
      <button type="button" onClick={() => onEdit(step)} data-testid={`${testid}-edit`}
        className="text-xs px-2.5 py-1 rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1">
        <Pencil className="w-3 h-3" /> Edit this
      </button>
    </div>
    {children}
  </section>
);

const StubsTable = ({ stubs }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid="overview-stubs-table">
      <thead className="text-zinc-500 uppercase tracking-wider">
        <tr>{['Pay date', 'Period', 'Check #', 'Gross', 'Taxes', 'Net', 'YTD gross'].map((h) => <th key={h} className="text-left py-1.5 pr-3 font-medium">{h}</th>)}</tr>
      </thead>
      <tbody>
        {stubs.map((s, i) => {
          const c = s.current || {};
          const taxes = c.totalTaxes || (c.federalTax || 0) + (c.stateTax || 0) + (c.socialSecurity || 0) + (c.medicare || 0) + (c.sdi || 0);
          return (
            <tr key={`${s.payDate}-${i}`} className="border-t border-zinc-800" data-testid={`overview-stub-${i}`}>
              <td className="py-1.5 pr-3 text-white">{pretty(s.payDate)}</td>
              <td className="py-1.5 pr-3 text-zinc-400 whitespace-nowrap">{pretty(s.periodStart)} – {pretty(s.periodEnd)}</td>
              <td className="py-1.5 pr-3 text-zinc-300 font-mono">{s.checkNumber || '—'}</td>
              <td className="py-1.5 pr-3 text-zinc-100 font-mono">{money(c.grossPay)}</td>
              <td className="py-1.5 pr-3 text-red-300 font-mono">{money(taxes)}</td>
              <td className="py-1.5 pr-3 text-emerald-300 font-mono">{money(c.netPay)}</td>
              <td className="py-1.5 pr-3 text-sky-300 font-mono">{money(s.ytd?.grossPay)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const usePdfPreview = (fd) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/paystub/generate-pdf`, {
        formData: { ...fd, paystubs: [fd.paystubs[0]], numberOfStubs: 1 },
        templateId: fd.selectedTemplate || 1,
        colorMode: fd.colorMode || 'original',
        accentHex: fd.colorMode === 'custom' ? fd.customColor : null,
        fontScale: fd.fontScale || 1.0,
        autoFit: fd.autoFit !== false,
      }, { responseType: 'blob' });
      setUrl(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
    } catch {
      toast.error('Could not build the preview');
    } finally {
      setBusy(false);
    }
  };
  const close = () => { if (url) URL.revokeObjectURL(url); setUrl(null); };
  return { url, busy, open, close };
};

export default function ClientOverview({ client, onBack, onEdit }) {
  const fd = client.formData || {};
  const stubs = fd.paystubs || [];
  const pdf = usePdfPreview(fd);
  const template = PAYSTUB_TEMPLATES.find((t) => t.id === Number(fd.selectedTemplate))?.name || `Template ${fd.selectedTemplate || 1}`;
  const pay = fd.payEntryMode === 'per-check'
    ? `${money(fd.perCheckAmount)} ${fd.perCheckType === 'net' ? 'net' : 'gross'} per check`
    : fd.paymentType === 'hourly' ? `${money(fd.hourlyRate)}/hr × ${fd.hoursPerPeriod || 0} hrs` : `${money(fd.annualSalary)} / year`;

  return (
    <div className="space-y-5" data-testid="client-overview">
      <button type="button" onClick={onBack} className="ml-12 md:ml-0 text-zinc-400 hover:text-white text-sm flex items-center gap-2" data-testid="overview-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500">Before you edit — everything saved so far</p>
          <h1 className="text-2xl font-bold text-white" data-testid="overview-client-name">{client.name}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {stubs.length > 0 && (
            <button type="button" onClick={pdf.open} disabled={pdf.busy} data-testid="overview-preview-pdf"
              className="h-10 px-4 rounded-lg border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 text-sm font-semibold flex items-center gap-2">
              {pdf.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
            </button>
          )}
          <button type="button" onClick={() => onEdit(0)} data-testid="overview-edit-start"
            className="h-10 px-4 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold">Edit from the start</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section icon={Palette} title="Template" step={0} onEdit={onEdit} testid="overview-template">
          <Row label="Template" value={template} />
          <Row label="Color" value={fd.colorMode === 'custom' ? `Custom ${fd.customColor || ''}` : fd.colorMode === 'bw' ? 'Black & white' : fd.colorScheme || 'Original'} />
        </Section>
        <Section icon={Building2} title="Company" step={1} onEdit={onEdit} testid="overview-company">
          <Row label="Name" value={fd.companyName} />
          <Row label="Address" value={join(fd.companyAddress1, fd.companyAddress2, fd.companyCity, [fd.companyState, fd.companyZip].filter(Boolean).join(' '))} />
          <Row label="Phone" value={fd.companyPhone} />
        </Section>
        <Section icon={User} title="Employee" step={2} onEdit={onEdit} testid="overview-employee">
          <Row label="Name" value={fd.employeeName} />
          <Row label="Address" value={join(fd.employeeAddress1, fd.employeeAddress2, fd.employeeCity, [fd.employeeState, fd.employeeZip].filter(Boolean).join(' '))} />
          <Row label="SSN (last 4)" value={fd.employeeSSN ? `•••-••-${String(fd.employeeSSN).slice(-4)}` : ''} />
          <Row label="Filing / exemptions" value={`${fd.filingStatus || 'single'} · ${fd.exemptions ?? 1}`} />
        </Section>
        <Section icon={Briefcase} title="Pay settings" step={3} onEdit={onEdit} testid="overview-pay">
          <Row label="Frequency" value={FREQ[fd.payFrequency] || fd.payFrequency} />
          <Row label="Pay" value={pay} />
          <Row label="Number of stubs" value={String(fd.numberOfStubs || stubs.length || 1)} />
          <Row label="Tax state" value={fd.taxState} />
        </Section>
      </div>

      <Section icon={Calculator} title={`Paystubs (${stubs.length})`} step={4} onEdit={onEdit} testid="overview-stubs">
        {stubs.length ? <StubsTable stubs={stubs} /> : <p className="text-sm text-zinc-500">No stubs generated yet.</p>}
      </Section>

      <button type="button" onClick={() => onEdit(5)} data-testid="overview-go-preview"
        className="w-full h-12 rounded-lg border border-zinc-700 text-zinc-200 hover:bg-zinc-800 text-sm font-semibold flex items-center justify-center gap-2">
        <FileCheck className="w-4 h-4" /> Go to Preview & Download
      </button>

      {pdf.url && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="overview-pdf-modal">
          <div className="w-full max-w-4xl h-[85vh] bg-zinc-900 rounded-xl overflow-hidden flex flex-col">
            <div className="flex justify-between items-center px-4 py-2 border-b border-zinc-800">
              <p className="text-sm text-white">Latest stub — {pretty(stubs[0]?.payDate)}</p>
              <button type="button" onClick={pdf.close} className="text-zinc-400 hover:text-white" data-testid="overview-pdf-close"><X className="w-5 h-5" /></button>
            </div>
            <iframe title="Paystub preview" src={pdf.url} className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
